import React from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation, gql } from '@apollo/client';
import { IntlProvider, useIntl } from 'react-intl';
import HiringPostForm from '../../components/StudentPlatform/pages/HiringPostForm';
import ApolloWrapper from './ApolloWrapper';
import getMessages from '../../lib/i18n/getMessages';
import { CurrencyProvider } from '../../hooks/useCurrency';

const t = (key) => getMessages(window.locale || 'en')[key];

const GET_HIRING_POST = gql`
  query GetHiringPost($id: ID!) {
    hiringPost(id: $id) {
      id
      title
      position_type
      location
      firm_name
      remote
      description
      requirements
      qualifications
      duration
      start_date
      compensation
      linkedin_url
      application_instructions
      status
    }
  }
`;

const CREATE_HIRING_POST = gql`
  mutation CreateHiringPost(
    $title: String!
    $position_type: String!
    $location: String!
    $firm_name: String
    $remote: Boolean
    $description: String!
    $requirements: String
    $qualifications: String
    $duration: String
    $start_date: ISO8601Date
    $compensation: String
    $linkedin_url: String
    $application_instructions: String
    $status: String
  ) {
    create_hiring_post(
      title: $title
      position_type: $position_type
      location: $location
      firm_name: $firm_name
      remote: $remote
      description: $description
      requirements: $requirements
      qualifications: $qualifications
      duration: $duration
      start_date: $start_date
      compensation: $compensation
      linkedin_url: $linkedin_url
      application_instructions: $application_instructions
      status: $status
    ) {
      id
      title
      status
    }
  }
`;

const UPDATE_HIRING_POST = gql`
  mutation UpdateHiringPost(
    $id: ID!
    $title: String
    $position_type: String
    $location: String
    $firm_name: String
    $remote: Boolean
    $description: String
    $requirements: String
    $qualifications: String
    $duration: String
    $start_date: ISO8601Date
    $compensation: String
    $linkedin_url: String
    $application_instructions: String
    $status: String
  ) {
    update_hiring_post(
      id: $id
      title: $title
      position_type: $position_type
      location: $location
      firm_name: $firm_name
      remote: $remote
      description: $description
      requirements: $requirements
      qualifications: $qualifications
      duration: $duration
      start_date: $start_date
      compensation: $compensation
      linkedin_url: $linkedin_url
      application_instructions: $application_instructions
      status: $status
    ) {
      id
      title
      status
    }
  }
`;

const DELETE_HIRING_POST = gql`
  mutation DeleteHiringPost($id: ID!) {
    delete_hiring_post(id: $id) {
      success
      message
    }
  }
`;

const HiringPostFormContainerInner = ({ postId }) => {
  const intl = useIntl();
  const isEditing = !!postId;

  const { loading, error, data } = useQuery(GET_HIRING_POST, {
    variables: { id: postId },
    skip: !isEditing,
  });

  const [createHiringPost] = useMutation(CREATE_HIRING_POST, {
    onCompleted: (data) => {
      console.log('Post created:', data.create_hiring_post);
      alert(t('hiring.container.form.published_success'));
      window.location.href = '/hiring';
    },
    onError: (error) => {
      console.error('Error creating post:', error);
      alert(`Error creating post: ${error.message}`);
    },
  });

  const [updateHiringPost] = useMutation(UPDATE_HIRING_POST, {
    onCompleted: (data) => {
      console.log('Post updated:', data.update_hiring_post);
      alert(t('hiring.container.form.updated_success'));
      window.location.href = '/hiring';
    },
    onError: (error) => {
      console.error('Error updating post:', error);
      alert(`Error updating post: ${error.message}`);
    },
  });

  const [deleteHiringPost] = useMutation(DELETE_HIRING_POST, {
    onCompleted: (data) => {
      console.log('Post deleted:', data.delete_hiring_post);
      alert(t('hiring.container.form.deleted_success'));
      window.location.href = '/hiring';
    },
    onError: (error) => {
      console.error('Error deleting post:', error);
      alert(`Error deleting post: ${error.message}`);
    },
  });

  const handleSave = async (formData) => {
    // Transform camelCase form data to snake_case for GraphQL
    const variables = {
      title: formData.title,
      position_type: formData.positionType,
      location: formData.location,
      firm_name: formData.firmName || null,
      remote: formData.remote,
      description: formData.description,
      requirements: formData.requirements || null,
      qualifications: formData.qualifications || null,
      duration: formData.duration || null,
      start_date: formData.startDate || null,
      compensation: formData.compensation || null,
      linkedin_url: formData.linkedinUrl || null,
      application_instructions: formData.applicationInstructions || null,
      status: formData.status || 'draft',
    };

    if (isEditing) {
      await updateHiringPost({
        variables: {
          id: postId,
          ...variables,
        },
      });
    } else {
      await createHiringPost({
        variables,
      });
    }
  };

  const handlePublish = async (formData) => {
    // Transform camelCase form data to snake_case for GraphQL
    const variables = {
      title: formData.title,
      position_type: formData.positionType,
      location: formData.location,
      firm_name: formData.firmName || null,
      remote: formData.remote,
      description: formData.description,
      requirements: formData.requirements || null,
      qualifications: formData.qualifications || null,
      duration: formData.duration || null,
      start_date: formData.startDate || null,
      compensation: formData.compensation || null,
      linkedin_url: formData.linkedinUrl || null,
      application_instructions: formData.applicationInstructions || null,
      status: formData.status || 'published',
    };

    if (isEditing) {
      await updateHiringPost({
        variables: {
          id: postId,
          ...variables,
        },
      });
    } else {
      await createHiringPost({
        variables,
      });
    }
  };

  const handleDelete = async () => {
    if (isEditing) {
      await deleteHiringPost({
        variables: { id: postId },
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-neutral-600">{intl.formatMessage({ id: 'hiring.container.form.loading' })}</p>
        </div>
      </div>
    );
  }

  if (error && isEditing) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-neutral-900 mb-2">{intl.formatMessage({ id: 'hiring.container.form.error_heading' })}</h2>
          <p className="text-neutral-600 mb-4">{error.message}</p>
          <a
            href="/hiring"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            {intl.formatMessage({ id: 'hiring.container.form.back_link' })}
          </a>
        </div>
      </div>
    );
  }

  // Transform snake_case GraphQL data to camelCase for the form component
  const transformedPost = data?.hiringPost ? {
    ...data.hiringPost,
    positionType: data.hiringPost.position_type,
    firmName: data.hiringPost.firm_name,
    startDate: data.hiringPost.start_date,
    linkedinUrl: data.hiringPost.linkedin_url,
    applicationInstructions: data.hiringPost.application_instructions,
  } : null;

  return (
    <HiringPostForm
      post={transformedPost}
      isEditing={isEditing}
      onSave={handleSave}
      onPublish={handlePublish}
      onDelete={handleDelete}
    />
  );
};

HiringPostFormContainerInner.propTypes = {
  postId: PropTypes.string,
};

const HiringPostFormContainer = ({ postId }, railsContext) => {
  window.locale = (railsContext && railsContext.i18nLocale) || window.locale || 'en';
  window.currency = (railsContext && railsContext.currency) || window.currency || 'USD';
  const locale = window.locale;
  return (
    <CurrencyProvider currency={(railsContext && railsContext.currency) || window.currency || 'USD'}>
      <IntlProvider locale={locale} messages={getMessages(locale)}>
        <ApolloWrapper>
          <HiringPostFormContainerInner postId={postId} />
        </ApolloWrapper>
      </IntlProvider>
    </CurrencyProvider>
  );
};

HiringPostFormContainer.propTypes = {
  postId: PropTypes.string,
};

export default HiringPostFormContainer;
